/**
 * @file notify.js
 * @location src/integrations/notify.js
 * @role 외부 알림 추상 인터페이스 — 슬랙 / 카카오워크 / 잔디 어댑터 진입점
 *
 * 백엔드가 아직 없으므로 어댑터는 console 만. 백엔드 도착 시:
 *   1) registerAdapter('lineworks', { send: (channel, msg) => fetch(...) })
 *   2) settings UI 가 S.integrations[adapter].enabled 토글
 *   3) 호출부 변경 없음 — notify() 만 살아있으면 즉시 동작
 *
 * 사용 예:
 *   import { notify, EVENTS } from '../integrations/notify';
 *   notify(EVENTS.TASK_ASSIGNED, { taskId, toUid, fromUid, title });
 */

export const EVENTS = Object.freeze({
  TASK_ASSIGNED:      'task.assigned',
  TASK_ACKNOWLEDGED:  'task.acknowledged',
  TASK_COMPLETED:     'task.completed',
  TASK_CANCELLED:     'task.cancelled',
});

/**
 * @typedef {Object} NotifyAdapter
 * @property {string} name                                            식별 이름 (예: 'console', 'lineworks')
 * @property {(event: string, payload: object) => Promise<void>|void} send
 * @property {(event: string) => boolean} [accepts]                   특정 event 만 받고 싶을 때
 */

/** @type {NotifyAdapter[]} */
const adapters = [];

import { debug } from '../utils/debug.js';

const consoleAdapter = {
  name: 'console',
  send(event, payload) {
    debug(`[notify:${event}]`, payload);
  },
};
adapters.push(consoleAdapter);

/** 어댑터 등록 — 백엔드 부팅 시 1회 호출하면 끝 */
export function registerAdapter(adapter) {
  if (!adapter || typeof adapter.send !== 'function') {
    throw new Error('notify: adapter must implement send(event, payload)');
  }
  const idx = adapters.findIndex(a => a.name === adapter.name);
  if (idx >= 0) adapters[idx] = adapter;
  else adapters.push(adapter);
}

/** 등록된 어댑터 목록 (테스트·디버깅 용) */
export function listAdapters() {
  return adapters.map(a => a.name);
}

/** 모든 어댑터 정리 — 테스트 외에는 호출 금지 */
export function _resetAdapters() {
  adapters.length = 0;
  adapters.push(consoleAdapter);
}

/**
 * 비즈니스 이벤트 발화. 어댑터별로 fire-and-forget — 실패해도 호출부 영향 없음.
 * 호출 시점은 UI 액션 boundary (reducer 가 아닌 곳).
 */
export function notify(event, payload = {}) {
  for (const a of adapters) {
    if (a.accepts && !a.accepts(event)) continue;
    try {
      const r = a.send(event, payload);
      if (r && typeof r.catch === 'function') r.catch(() => {});
    } catch {
      // adapter 실패는 호출부에 전파하지 않는다
    }
  }
}
